import React, { useEffect, useState } from "react";
import { Link, NavLink, useLocation } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { Menu, X, ChevronDown } from "lucide-react";
import { useLanguage } from "@/i18n/LanguageContext";

import { NandaSaMark } from "@/components/BrandMarks";

// Menu B2B 2026 — hiérarchie industrielle & multisecteur avant les marques premium.
// Structure :
//   Solutions ▼  (mega-menu B2B : Réfrigération, Énergies renouvelables, Matières premières, Cajou)
//   Conseil
//   Marques ▼    (mega-menu Maisons premium)
//   Opportunités
//   Carnet
//   Contact

const T = {
  entreprise: { fr: "Entreprise", en: "Company", pt: "Empresa" },
  poles: { fr: "Nos pôles", en: "Our divisions", pt: "As nossas divisões" },
  opportunites: { fr: "Opportunités", en: "Opportunities", pt: "Oportunidades" },
  carnet: { fr: "Carnet", en: "Journal", pt: "Caderno" },
  contact: { fr: "Contact", en: "Contact", pt: "Contacto" },
  maison: { fr: "La Maison", en: "The House", pt: "A Casa" },
};

// Menu Entreprise (pages institutionnelles)
const ENTREPRISE_ITEMS = [
  {
    to: "/maison",
    label: { fr: "À propos", en: "About", pt: "Sobre nós" },
    hint: { fr: "La Maison NANDA SÁ · notre histoire", en: "The NANDA SÁ House · our story", pt: "A Casa NANDA SÁ · a nossa história" },
  },
  {
    to: "/marques/academie",
    label: { fr: "Académie", en: "Academy", pt: "Academia" },
    hint: { fr: "Formation, savoirs, transmission", en: "Training, know-how, transmission", pt: "Formação, saberes, transmissão" },
  },
  {
    to: "/conseil-implantation",
    label: { fr: "Conseil", en: "Consulting", pt: "Consultoria" },
    hint: { fr: "Accompagnement à l'implantation", en: "Setup & advisory services", pt: "Acompanhamento à implantação" },
  },
  {
    to: "/engagement-odd",
    label: { fr: "Développement durable (ODD)", en: "Sustainability (SDG)", pt: "Desenvolvimento sustentável (ODS)" },
    hint: { fr: "Notre engagement pour les 17 Objectifs", en: "Our commitment to the 17 Goals", pt: "O nosso compromisso com os 17 Objectivos" },
  },
];

// Menu Nos pôles (les 4 divisions opérationnelles)
const POLES_ITEMS = [
  {
    to: "/energies-renouvelables",
    label: { fr: "Énergies renouvelables", en: "Renewable energy", pt: "Energias renováveis" },
    hint: { fr: "Kits solaires · Batteries · Onduleurs · Panneaux", en: "Solar kits · Batteries · Inverters · Panels", pt: "Kits solares · Baterias · Inversores · Painéis" },
  },
  {
    to: "/refrigeration-climatisation",
    label: { fr: "Réfrigération & Climatisation", en: "Refrigeration & AC", pt: "Refrigeração & Climatização" },
    hint: { fr: "Chambres froides · Froid pro · Climatisation", en: "Cold rooms · Commercial refrigeration · AC", pt: "Câmaras frias · Frio comercial · Climatização" },
  },
  {
    to: "/cosmetiques",
    label: { fr: "Matières premières naturelles & Cosmétiques", en: "Natural raw materials & Cosmetics", pt: "Matérias-primas naturais & Cosméticos" },
    hint: { fr: "Karité · Cacao · Huiles · Extraits · Savons", en: "Shea · Cocoa · Oils · Extracts · Soaps", pt: "Karité · Cacau · Óleos · Extractos · Sabonetes" },
  },
  {
    to: "/produits-consommables",
    label: { fr: "Agroalimentaire & Produits du terroir", en: "Food & Terroir products", pt: "Agroalimentar & Produtos do terroir" },
    hint: { fr: "Fleur de sel · Miel · Baobab · Hibiscus · Moringa", en: "Fleur de sel · Honey · Baobab · Hibiscus · Moringa", pt: "Flor de sal · Mel · Baobá · Hibisco · Moringa" },
  },
];

const MARQUES_ITEMS = [
  {
    to: "/marques/energia-pro",
    label: { fr: "NANDA SÁ ENERGIA PRO", en: "NANDA SÁ ENERGIA PRO", pt: "NANDA SÁ ENERGIA PRO" },
    hint: { fr: "Solaire · Équipement · EPI", en: "Solar · Equipment · PPE", pt: "Solar · Equipamento · EPI" },
  },
  {
    to: "/marques/nanda-sa",
    label: { fr: "NANDA SÁ", en: "NANDA SÁ", pt: "NANDA SÁ" },
    hint: { fr: "Épicerie fine · Anacarde", en: "Fine foods · Cashew", pt: "Mercearia fina · Caju" },
  },
  {
    to: "/marques/academie",
    label: { fr: "NANDA SÁ Académie", en: "NANDA SÁ Académie", pt: "NANDA SÁ Académie" },
    hint: { fr: "Éducation · Orientation · Numérique", en: "Education · Guidance · Digital", pt: "Educação · Orientação · Digital" },
  },
];

const NavItem = ({ to, label, onClick, testId }) => (
  <NavLink
    to={to}
    onClick={onClick}
    data-testid={testId}
    className={({ isActive }) =>
      `link-underline label-overline transition-colors duration-300 hover:text-nanda-ink ${
        isActive ? "text-nanda-ink" : "text-nanda-inkSoft"
      }`
    }
  >
    {label}
  </NavLink>
);

const Dropdown = ({ label, items, locale, testId }) => {
  const [open, setOpen] = useState(false);
  return (
    <div
      className="relative"
      onMouseEnter={() => setOpen(true)}
      onMouseLeave={() => setOpen(false)}
    >
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        onFocus={() => setOpen(true)}
        onBlur={(e) => { if (!e.currentTarget.parentElement.contains(e.relatedTarget)) setOpen(false); }}
        data-testid={testId}
        className="link-underline label-overline transition-colors duration-300 text-nanda-inkSoft hover:text-nanda-ink flex items-center gap-1.5"
        aria-expanded={open}
        aria-haspopup="true"
      >
        {label}
        <ChevronDown size={12} strokeWidth={2} className={`transition-transform ${open ? "rotate-180" : ""}`} />
      </button>
      <AnimatePresence>
        {open && (
          <motion.div
            initial={{ opacity: 0, y: 6 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: 6 }}
            transition={{ duration: 0.2 }}
            className="absolute left-1/2 -translate-x-1/2 top-full mt-3 w-[380px] bg-nanda-bg border border-nanda-line shadow-xl p-2"
            role="menu"
          >
            {items.map((it) => (
              <NavLink
                key={it.to}
                to={it.to}
                role="menuitem"
                data-testid={`dropdown-${it.to.replace(/\//g, "-")}`}
                className={({ isActive }) =>
                  `block px-5 py-4 group hover:bg-nanda-bgAlt transition-colors ${isActive ? "bg-nanda-bgAlt" : ""}`
                }
              >
                <div className="font-serif text-lg text-nanda-ink leading-tight">{it.label[locale] || it.label.fr}</div>
                <div className="mt-1 text-xs text-nanda-muted label-overline">{it.hint[locale] || it.hint.fr}</div>
              </NavLink>
            ))}
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
};

const LanguageSwitcher = ({ vertical = false }) => {
  const { locale, setLocale, locales } = useLanguage();
  return (
    <div className={`flex ${vertical ? "flex-row gap-3" : "items-center gap-1"} label-overline`} data-testid="language-switcher">
      {locales.map((l, i) => (
        <React.Fragment key={l}>
          <button
            type="button"
            onClick={() => setLocale(l)}
            data-testid={`lang-${l}`}
            className={`transition-colors duration-300 ${
              locale === l ? "text-nanda-ink" : "text-nanda-muted hover:text-nanda-inkSoft"
            }`}
          >
            {l.toUpperCase()}
          </button>
          {!vertical && i < locales.length - 1 && <span className="text-nanda-muted/60">·</span>}
        </React.Fragment>
      ))}
    </div>
  );
};

export default function Header() {
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);
  const [mobileSolutions, setMobileSolutions] = useState(false);
  const [mobileMarques, setMobileMarques] = useState(false);
  const location = useLocation();
  const { locale } = useLanguage();
  const t = (key) => T[key][locale] || T[key].fr;

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 12);
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  useEffect(() => { setOpen(false); }, [location.pathname]);

  return (
    <header
      data-testid="site-header"
      className={`fixed top-0 inset-x-0 z-50 transition-all duration-500 ${
        scrolled ? "bg-nanda-bg/85 backdrop-blur-xl border-b border-nanda-line" : "bg-transparent border-b border-transparent"
      }`}
    >
      <div className="mx-auto max-w-[1400px] px-6 md:px-10 lg:px-14 h-16 md:h-20 flex items-center justify-between gap-6">
        <Link to="/" data-testid="logo-link" className="flex items-center gap-3 group shrink-0">
          <NandaSaMark size={28} className="text-nanda-ink" />
          <span className="font-serif text-2xl md:text-[28px] tracking-tight text-nanda-ink">NANDA SÁ</span>
        </Link>

        <nav className="hidden lg:flex items-center gap-7 xl:gap-9">
          <Dropdown label={t("entreprise")} items={ENTREPRISE_ITEMS} locale={locale} testId="nav-entreprise" />
          <Dropdown label={t("poles")} items={POLES_ITEMS} locale={locale} testId="nav-poles" />
          <NavItem to="/opportunites" label={t("opportunites")} testId="nav-opportunites" />
          <NavItem to="/carnet" label={t("carnet")} testId="nav-carnet" />
          <NavItem to="/contact" label={t("contact")} testId="nav-contact" />
        </nav>

        <div className="hidden lg:flex shrink-0">
          <LanguageSwitcher />
        </div>

        <button
          data-testid="menu-toggle"
          aria-label={open ? "Fermer" : "Menu"}
          className="lg:hidden text-nanda-ink"
          onClick={() => setOpen((v) => !v)}
        >
          {open ? <X size={22} /> : <Menu size={22} />}
        </button>
      </div>

      <AnimatePresence>
        {open && (
          <motion.div
            initial={{ opacity: 0, y: -8 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -8 }}
            transition={{ duration: 0.3 }}
            className="lg:hidden bg-nanda-bg border-b border-nanda-line max-h-[85vh] overflow-y-auto"
            data-testid="mobile-menu"
          >
            <div className="px-6 py-8 flex flex-col gap-5">
              {/* Entreprise */}
              <div>
                <button
                  type="button"
                  onClick={() => setMobileSolutions((v) => !v)}
                  className="flex items-center justify-between w-full label-overline text-nanda-ink"
                  data-testid="mobile-entreprise-toggle"
                >
                  <span>{t("entreprise")}</span>
                  <ChevronDown size={14} strokeWidth={2} className={`transition-transform ${mobileSolutions ? "rotate-180" : ""}`} />
                </button>
                <AnimatePresence>
                  {mobileSolutions && (
                    <motion.div initial={{ height: 0, opacity: 0 }} animate={{ height: "auto", opacity: 1 }} exit={{ height: 0, opacity: 0 }} className="overflow-hidden">
                      <div className="mt-4 pl-4 border-l border-nanda-line flex flex-col gap-4">
                        {ENTREPRISE_ITEMS.map((it) => (
                          <NavLink key={it.to} to={it.to} onClick={() => setOpen(false)} className="text-nanda-inkSoft hover:text-nanda-ink" data-testid={`mobile-${it.to.replace(/\//g, "-")}`}>
                            <div className="font-serif text-lg">{it.label[locale] || it.label.fr}</div>
                            <div className="mt-1 text-xs text-nanda-muted label-overline">{it.hint[locale] || it.hint.fr}</div>
                          </NavLink>
                        ))}
                      </div>
                    </motion.div>
                  )}
                </AnimatePresence>
              </div>

              {/* Nos pôles */}
              <div>
                <button
                  type="button"
                  onClick={() => setMobileMarques((v) => !v)}
                  className="flex items-center justify-between w-full label-overline text-nanda-ink"
                  data-testid="mobile-poles-toggle"
                >
                  <span>{t("poles")}</span>
                  <ChevronDown size={14} strokeWidth={2} className={`transition-transform ${mobileMarques ? "rotate-180" : ""}`} />
                </button>
                <AnimatePresence>
                  {mobileMarques && (
                    <motion.div initial={{ height: 0, opacity: 0 }} animate={{ height: "auto", opacity: 1 }} exit={{ height: 0, opacity: 0 }} className="overflow-hidden">
                      <div className="mt-4 pl-4 border-l border-nanda-line flex flex-col gap-4">
                        {POLES_ITEMS.map((it) => (
                          <NavLink key={it.to} to={it.to} onClick={() => setOpen(false)} className="text-nanda-inkSoft hover:text-nanda-ink" data-testid={`mobile-${it.to.replace(/\//g, "-")}`}>
                            <div className="font-serif text-lg">{it.label[locale] || it.label.fr}</div>
                            <div className="mt-1 text-xs text-nanda-muted label-overline">{it.hint[locale] || it.hint.fr}</div>
                          </NavLink>
                        ))}
                      </div>
                    </motion.div>
                  )}
                </AnimatePresence>
              </div>

              <NavItem to="/opportunites" label={t("opportunites")} onClick={() => setOpen(false)} testId="mobile-nav-opportunites" />
              <NavItem to="/carnet" label={t("carnet")} onClick={() => setOpen(false)} testId="mobile-nav-carnet" />
              <NavItem to="/contact" label={t("contact")} onClick={() => setOpen(false)} testId="mobile-nav-contact" />

              <div className="pt-4 border-t border-nanda-line">
                <LanguageSwitcher vertical />
              </div>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </header>
  );
}
